import { Activity, FileText, Mail, MessageCircleQuestion, MessageSquare, Pencil } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PrototypeCard } from "./PrototypeCard";
import { StatusTimeline } from "./StatusTimeline";
import { canManage, useProjectContext } from "./projectContext";
import { errorMessage, fieldError } from "@/api/errors";
import { useFeedback } from "@/api/hooks/feedback";
import { useFiles } from "@/api/hooks/files";
import { useMembers } from "@/api/hooks/orgs";
import { usePatchProject, type ProjectOut } from "@/api/hooks/projects";
import { Field, FormError } from "@/components/Field";
import { MarkdownField } from "@/components/MarkdownField";
import { Avatar } from "@/components/ui/avatar";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Markdown } from "@/lib/markdown";

function QuickCard({ to, icon, title, value }: { to: string; icon: ReactNode; title: string; value: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-lg border border-border bg-surface p-5 no-underline shadow-card transition-shadow duration-base hover:shadow-elevated"
    >
      <span
        aria-hidden="true"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-subtle text-heading"
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="font-heading font-semibold text-heading">{title}</span>
        <span className="line-clamp-2 text-sm text-fg-muted">{value}</span>
      </span>
    </Link>
  );
}

/** The single most useful thing to do or know right now, in plain language. */
function NextStepCard({ project, base }: { project: ProjectOut; base: string }) {
  const { t } = useTranslation();
  const pending = project.pending_required_count;
  const status = project.status;
  const ready = project.questionnaire_ready_to_submit;
  const questions = pending > 0 && !ready;
  return (
    <Card className="border-l-4 border-l-brand">
      <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t("project.nextStep.title")}</p>
          <h2 className="text-xl">
            {ready
              ? t("project.nextStep.ready")
              : questions
                ? t("project.nextStep.questions", { count: pending })
                : t(`project.nextStep.${status}`)}
          </h2>
          <p className="text-fg-muted">
            {ready
              ? t("project.nextStep.readyBody")
              : questions
                ? t("project.nextStep.questionsBody")
                : t(`project.nextStep.${status}Body`)}
          </p>
        </div>
        {ready || questions ? (
          <Button asChild className="shrink-0">
            <Link to={`${base}/questionnaire?resume=1`} className="no-underline">
              {ready ? t("project.nextStep.review") : t("project.nextStep.continue")}
            </Link>
          </Button>
        ) : (
          <Button asChild variant="secondary" className="shrink-0">
            <Link to={`${base}/files`} className="no-underline">
              {status === "requested" ? t("project.nextStep.addFiles") : t("project.nextStep.openFiles")}
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function EditDetailsDialog({
  project,
  org,
  projectSlug,
  onClose,
}: {
  project: ProjectOut;
  org: string;
  projectSlug: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const members = useMembers(org);
  const patch = usePatchProject(org, projectSlug);
  const canRename = project.status === "requested";
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const [goals, setGoals] = useState(project.goals);
  const [contact, setContact] = useState(project.primary_contact_user_id ?? "");
  const [nameError, setNameError] = useState<string | null>(null);

  const save = () => {
    if (canRename && !name.trim()) {
      setNameError(t("project.edit.nameRequired"));
      return;
    }
    setNameError(null);
    patch.mutate(
      {
        ...(canRename && name.trim() !== project.name ? { name: name.trim() } : {}),
        description,
        goals,
        ...(contact && contact !== project.primary_contact_user_id ? { primary_contact_user_id: contact } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("project.edit.saved"));
          onClose();
        },
      },
    );
  };

  const nameServerError = fieldError(patch.error, "name");
  const contactError = fieldError(patch.error, "primary_contact_user_id");
  const serverError = patch.isError && !nameServerError && !contactError ? errorMessage(patch.error, t("errors.generic")) : null;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("project.edit.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("project.edit.title")}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          {canRename ? (
            <Field label={t("project.edit.name")} hint={t("project.edit.nameHint")} error={nameError ?? nameServerError}>
              {(p) => <Input {...p} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />}
            </Field>
          ) : null}
          <MarkdownField label={t("project.edit.description")} hint={t("markdown.hint")} value={description} onChange={setDescription} />
          <MarkdownField label={t("project.edit.goals")} value={goals} onChange={setGoals} rows={4} />
          <Field label={t("project.edit.contact")} hint={t("project.edit.contactHint")} error={contactError}>
            {(p) => (
              <Select {...p} className="w-full" value={contact} onChange={(e) => setContact(e.target.value)}>
                <option value="">{t("project.edit.noContact")}</option>
                {(members.data ?? []).map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name} ({m.email})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <FormError message={serverError} />
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={patch.isPending}>
              {patch.isPending ? t("project.edit.saving") : t("project.edit.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectOverviewPage() {
  const { t } = useTranslation();
  const { project, org, projectSlug, role } = useProjectContext();
  const location = useLocation();
  const files = useFiles(org, projectSlug);
  const feedback = useFeedback(org, projectSlug);
  const [editing, setEditing] = useState(false);
  const base = `/${org}/${projectSlug}`;
  const pending = project.pending_required_count;
  const onTimeline = ["discovery", "design", "build", "live"].includes(project.status);
  const created = location.state as { created?: boolean; failedUploads?: number } | null;
  const justCreated = Boolean(created?.created);
  const failedUploads = created?.failedUploads ?? 0;
  const editable = canManage(role);
  const typeText = project.project_type
    ? project.project_type === "other" && project.project_type_other
      ? project.project_type_other
      : t(`project.type.${project.project_type}`)
    : null;

  return (
    <div className="flex flex-col gap-6">
      {justCreated ? <Banner tone="success">{t("project.created")}</Banner> : null}
      {failedUploads > 0 ? <Banner tone="warning">{t("newProject.uploadFailed", { count: failedUploads })}</Banner> : null}

      <NextStepCard project={project} base={base} />

      <PrototypeCard org={org} projectSlug={projectSlug} role={role} />

      <div className="grid gap-6 lg:grid-cols-main">
        <Card>
          <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl">{t("project.about")}</h2>
              {typeText ? <p className="text-sm text-fg-muted">{t("project.typeLabel", { type: typeText })}</p> : null}
            </div>
            {editable ? (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <Pencil aria-hidden="true" className="h-4 w-4" />
                {t("project.edit.button")}
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            {project.description ? (
              <Markdown source={project.description} />
            ) : (
              <p className="text-fg-muted">{t("project.noDescription")}</p>
            )}
            {project.goals ? (
              <div>
                <h3 className="mb-2">{t("project.goals")}</h3>
                <Markdown source={project.goals} />
              </div>
            ) : null}
          </CardContent>
        </Card>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <h2 className="text-xl">{t("project.primaryContact")}</h2>
            </CardHeader>
            <CardContent>
              {project.primary_contact ? (
                <div className="flex items-center gap-3">
                  <Avatar name={project.primary_contact.name} />
                  <div className="flex min-w-0 flex-col">
                    <span className="font-semibold">{project.primary_contact.name}</span>
                    <a href={`mailto:${project.primary_contact.email}`} className="inline-flex items-center gap-2 break-all text-sm">
                      <Mail aria-hidden="true" className="h-4 w-4 shrink-0" />
                      {project.primary_contact.email}
                    </a>
                  </div>
                </div>
              ) : (
                <p className="text-fg-muted">{t("project.noContact")}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <h2 className="text-xl">{t("project.timeline")}</h2>
            </CardHeader>
            <CardContent className="pt-4">
              <StatusTimeline status={project.status} />
              {!onTimeline ? (
                <p className="mt-4 text-sm text-fg-muted">{t("project.statusNote", { status: t(`project.status.${project.status}`) })}</p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <QuickCard
          to={`${base}/questionnaire`}
          icon={<MessageCircleQuestion className="h-5 w-5" />}
          title={t("project.quick.questionnaire")}
          value={
            project.questionnaire_ready_to_submit
              ? t("project.readyToSubmit")
              : pending > 0
                ? t("project.questionsWaiting", { count: pending })
                : t("project.allAnswered")
          }
        />
        <QuickCard
          to={`${base}/files`}
          icon={<FileText className="h-5 w-5" />}
          title={t("project.quick.files")}
          value={files.data ? t("project.filesCount", { count: files.data.length }) : t("common.loading")}
        />
        <QuickCard
          to={`${base}/feedback`}
          icon={<MessageSquare className="h-5 w-5" />}
          title={t("project.quick.feedback")}
          value={feedback.data ? t("project.feedbackCount", { count: feedback.data.length }) : t("common.loading")}
        />
        <QuickCard
          to={`${base}/activity`}
          icon={<Activity className="h-5 w-5" />}
          title={t("project.quick.activity")}
          value={project.latest_activity ?? t("project.quick.activityValue")}
        />
      </div>

      {editable && editing ? (
        <EditDetailsDialog project={project} org={org} projectSlug={projectSlug} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  );
}
